// src/components/lead/sections/TrainingContactResultCard.tsx
import type { ContactResult } from "@/lib/contacts/types";
import { cardTitleTextClass, insetSurfaceCardClass, secondaryBtnClass } from "@/components/common/surfaceClasses";
import { logContactRoutingSelection } from "@/lib/contacts/router";
import { ContactTopicTagsReadonly } from "@/components/contact-directory/ContactTopicTagsReadonly";
import {
  contactCardLabel,
  contactEmail,
  hasDirectContactInfo,
  normalizedContactTopics,
} from "@/components/lead/sections/trainingContactsDirectoryShared";

export function TrainingContactResultCard(props: {
  c: ContactResult;
  organizationId: string | null | undefined;
  /** Text stored with routing analytics (search box or ask thread query). */
  logQuery: string;
  recommended: ContactResult | null;
}) {
  const { c, organizationId, logQuery, recommended } = props;
  const label = contactCardLabel(c);
  const hasDirect = hasDirectContactInfo(c);
  const email = contactEmail(c);
  const canMessageUser = c.source === "user" && !!email;
  const topics = normalizedContactTopics(c);
  const companyLine = [c.company, c.location].filter((x) => String(x || "").trim()).join(" · ") || null;

  return (
    <div className={`${insetSurfaceCardClass} flex flex-col sm:flex-row gap-3 sm:gap-4`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <div className={`text-sm font-semibold ${cardTitleTextClass}`}>{label}</div>
          {c.recommended ? (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800">
              Recommended
            </span>
          ) : null}
        </div>

        <div className="text-xs text-gray-600 dark:text-zinc-400">
          {c.source ? `${c.tab} (${c.source})` : c.tab}
          {c.scope ? ` • ${c.scope}` : ""}
          {c.contactPath ? ` • ${c.contactPath}` : ""}
          {c.department ? ` • ${c.department}` : ""}
        </div>

        {companyLine ? <div className="text-xs text-gray-800"><span className="text-gray-500">Company / location:</span> {companyLine}</div> : null}
        {c.title?.trim() && !c.name?.trim() ? (
          <div className="text-xs text-gray-800">
            <span className="text-gray-500">Title:</span> {c.title}
          </div>
        ) : null}

        <div className="text-xs text-gray-700 space-y-0.5">
          {!hasDirect ? (
            <div className="text-amber-700">
              {c.contactPath ? `Route via: ${c.contactPath}` : "Contact through the proper chain of command."}
            </div>
          ) : (
            <>
              {c.phone?.trim() ? <div>Phone: {c.phone}</div> : null}
              {c.personEmail?.trim() ? <div>Email: {c.personEmail}</div> : null}
              {!c.personEmail?.trim() && c.email?.trim() ? <div>Email: {c.email}</div> : null}
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            className={`${secondaryBtnClass} px-2 py-1 text-[11px]`}
            onClick={async () => {
              const needsOverride =
                !!recommended && !!recommended.sourceId && recommended.sourceId !== c.sourceId;
              const reason = needsOverride
                ? window.prompt("Override reason (optional but recommended):", "") || ""
                : "";
              await logContactRoutingSelection({
                organizationId,
                query: logQuery.trim() || "contacts_directory_open",
                pathKey: c.contactPath || recommended?.contactPath || null,
                chosenContactId: c.sourceId || null,
                chosenContactSource: c.source || null,
                recommendedContactId: recommended?.sourceId || null,
                recommendedContactSource: recommended?.source || null,
                overrideReason: reason.trim() || null,
              });
              if (canMessageUser) window.location.href = `mailto:${email}`;
            }}
          >
            {canMessageUser ? "Send message" : "Use this contact"}
          </button>
          {c.source !== "user" && c.phone ? (
            <a
              href={`tel:${c.phone}`}
              className={`inline-flex ${secondaryBtnClass} px-2 py-1 text-[11px]`}
            >
              Call
            </a>
          ) : null}
          {c.source !== "user" && email ? (
            <a
              href={`mailto:${email}`}
              className={`inline-flex ${secondaryBtnClass} px-2 py-1 text-[11px]`}
            >
              Email
            </a>
          ) : null}
        </div>
      </div>

      <div className="shrink-0 sm:w-[200px] lg:w-[220px] sm:border-l sm:border-gray-100 sm:pl-4 pt-2 sm:pt-0 border-t border-gray-100 sm:border-t-0 dark:sm:border-zinc-800 dark:border-zinc-800">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-1.5">Topic tags</div>
        <ContactTopicTagsReadonly topics={topics} align="end" />
      </div>
    </div>
  );
}
